/**
 * Vertical-drag dismissal for sheets/dialogs/popovers.
 *
 * Same gesture standard as the rest of the product (`@/lib/gestures`):
 * recognizer with deadzone + Y-axis lock, fling, rubber-band, and respect for
 * `prefers-reduced-motion`. Mirror of the vertical half of listto's drawer
 * carousel (pull-to-close), extracted into a reusable hook.
 *
 * It only controls downward movement and only when inner scrolling is at the
 * top (`canEngage`), so content scrolling never breaks. It starts only on
 * non-interactive areas (or the `.drag-handle-bar`), and aborts if text editing
 * is active: it never steals the cursor.
 */
import { createSignal } from "solid-js";

import { closestSelector, isEditableTarget, isEditingActive } from "@/lib/gestures/guards";
import { prefersReducedMotion } from "@/lib/gestures/motion";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { SHEET_DISMISS } from "@/lib/gestures/presets";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  transitionMs: TRANSITION_MS,
  fallbackHeightPx: FALLBACK_HEIGHT_PX,
  hapticMs: HAPTIC_MS,
  fling: FLING,
} = SHEET_DISMISS;

/** Selector for controls that must NOT start drag dismissal. */
const INTERACTIVE_SELECTOR = "input, select, textarea, button, [role='button'], a";

interface PullToDismissOptions {
  /** Closes the sheet/dialog once the drag is confirmed. */
  onDismiss: () => void;
  /** Allows disabling the gesture (e.g. non-dismissible dialogs). */
  disabled?: () => boolean;
  /** Dismissible surface: measures height for the exit animation and the
   *  inner-scroll guard. */
  getSurfaceRef: () => HTMLElement | undefined;
}

export function usePullToDismiss(options: PullToDismissOptions) {
  const [translateY, setTranslateY] = createSignal(0);
  const [isAnimating, setIsAnimating] = createSignal(false);

  const height = () => options.getSurfaceRef()?.clientHeight ?? FALLBACK_HEIGHT_PX;

  const settle = () => {
    if (prefersReducedMotion()) {
      setTranslateY(0);
      return;
    }
    setIsAnimating(true);
    setTranslateY(0);
    setTimeout(() => {
      setIsAnimating(false);
    }, TRANSITION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "y",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_MS,
    fling: { ...FLING },
    canStart: ({ target }) => {
      if (options.disabled?.() || isEditingActive() || isEditableTarget(target)) {
        return false;
      }
      // Do not steal starts on controls, except on the drag handle itself.
      if (closestSelector(target, ".drag-handle-bar")) {
        return true;
      }
      return !closestSelector(target, INTERACTIVE_SELECTOR);
    },
    canEngage: (_axis, sample) => {
      // Only downward, and only when there is no inner scroll left above.
      if (sample.dy <= 0) {
        return false;
      }
      const surface = options.getSurfaceRef();
      const scroller = surface?.querySelector<HTMLElement>(".overflow-y-auto, .overflow-auto");
      return (scroller?.scrollTop ?? 0) <= 0;
    },
    onStart: () => {
      setIsAnimating(false);
    },
    onMove: (sample) => {
      // Only downward; rubber-band for premium resistance.
      const next = applyRubberBand(Math.max(0, sample.dy), 0, RUBBER_BAND_FACTOR);
      setTranslateY(next);
    },
    onEnd: (sample) => {
      const committed = sample.isFling || translateY() > COMMIT_THRESHOLD_PX;
      if (committed) {
        if (prefersReducedMotion()) {
          setTranslateY(0);
          options.onDismiss();
          return;
        }
        setIsAnimating(true);
        setTranslateY(height());
        setTimeout(() => {
          options.onDismiss();
          setIsAnimating(false);
          setTranslateY(0);
        }, TRANSITION_MS);
        return;
      }
      settle();
    },
    onCancel: settle,
  });

  const base = pointerHandlers(recognizer);

  return {
    translateY,
    isAnimating,
    transitionMs: TRANSITION_MS,
    indicatorTriggerPx: SHEET_DISMISS.indicatorTriggerPx,
    commitThresholdPx: COMMIT_THRESHOLD_PX,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
  };
}
